import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FiInfo } from 'react-icons/fi'
import { TicketStatusBadge } from '../TicketStatusCard'
import { TicketStatus } from '../TicketStatusCard/TicketStatusBadge.types'
import Tooltip from '../Tooltip'
import { isEmptyRewardResult } from '../../utils/rewardResultUtils'
import {
  isSetLotteryBetType,
  formatMatchType,
} from '../../types/setLottery.types'
import { computeFinalPayout } from './computeFinalPayout'
import type { WinningNumberGroup } from './TicketDetails'
import WinningNumbersCollapsible from './WinningNumbersCollapsible'
import { padDigits } from '../../utils/datetimeFormatUtils'
import styles from './BetDetailCard.module.css'

type FooterVariant = 'default' | 'win' | 'lose'

export interface BetDetailCardProps {
  betType: string
  rawBetType?: string
  rowNumber: string | number
  status: TicketStatus
  betNumber: string
  rewardResult?: number | string
  payoutRate: number
  limitedPayoutRate?: number
  betAmount: number
  finalPayout?: number
  /** Set lottery: match type key, e.g. '6_digit_match', 'no_match' */
  matchType?: string
  /** Set lottery: maximum possible reward amount */
  maxRewardAmount?: number
  /** Set lottery: collapsible winning number groups for the draw */
  winningNumbers?: WinningNumberGroup[]
}

const BetDetailCard: React.FC<BetDetailCardProps> = ({
  betType,
  rawBetType,
  rowNumber,
  status,
  betNumber,
  rewardResult,
  payoutRate,
  limitedPayoutRate,
  betAmount,
  finalPayout,
  matchType,
  maxRewardAmount,
  winningNumbers,
}) => {
  // Check if this is "Bet Three Tod" type which has multiple reward results
  const isThreeTodBet = rawBetType === 'bet_three_tod'

  // Check if this is a set lottery bet type
  const isSetLottery = isSetLotteryBetType(rawBetType)

  // Parse multiple results for "Bet Three Tod"
  const multipleResults =
    isThreeTodBet && rewardResult
      ? String(rewardResult)
          .split(/[\n,]/)
          .map((r) => r.trim())
          .filter((r) => r)
      : []
  const { t } = useTranslation()

  const hasLimitedRate =
    limitedPayoutRate !== undefined && limitedPayoutRate !== payoutRate

  // Determine footer variant based on status
  const footerVariant: FooterVariant = useMemo(() => {
    if (status === TicketStatus.WIN) return 'win'
    if (status === TicketStatus.LOSE) return 'lose'
    return 'default'
  }, [status])

  // Compute final payout using shared utility (capped at maxRewardAmount for set lottery)
  const computedPayout = computeFinalPayout({
    finalPayout,
    payoutRate,
    limitedPayoutRate,
    betAmount,
    maxRewardAmount,
    rawBetType,
  })

  // Get border color based on status
  const borderColor =
    footerVariant === 'win'
      ? 'var(--bet-detail-win-border)'
      : footerVariant === 'lose'
        ? 'var(--bet-detail-lose-border)'
        : 'var(--ticket-info-card-border)'

  return (
    <div className="w-full relative overflow-visible">
      {/* Mobile punch hole border - left side (15px radius semicircle) */}
      <div
        className="sm:hidden absolute rounded-r-full border-r border-t border-b pointer-events-none z-10"
        style={{
          borderColor: borderColor,
          width: '15px',
          height: '30px',
          left: '0',
          bottom: '71px',
        }}
      />
      {/* Mobile punch hole border - right side (15px radius semicircle) */}
      <div
        className="sm:hidden absolute rounded-l-full border-l border-t border-b pointer-events-none z-10"
        style={{
          borderColor: borderColor,
          width: '15px',
          height: '30px',
          right: '0',
          bottom: '71px',
        }}
      />
      {/* Desktop punch hole border - left side (18px radius semicircle) */}
      <div
        className="hidden sm:block absolute rounded-r-full border-r border-t border-b pointer-events-none z-10"
        style={{
          borderColor: borderColor,
          width: '18px',
          height: '36px',
          left: '0',
          bottom: '77px',
        }}
      />
      {/* Desktop punch hole border - right side (18px radius semicircle) */}
      <div
        className="hidden sm:block absolute rounded-l-full border-l border-t border-b pointer-events-none z-10"
        style={{
          borderColor: borderColor,
          width: '18px',
          height: '36px',
          right: '0',
          bottom: '77px',
        }}
      />

      {/* Card Container with border and punch-hole mask */}
      <div
        className={`w-full bg-[var(--bet-detail-card-bg)] rounded-xl shadow-md border overflow-hidden ${
          footerVariant === 'win'
            ? 'border-[var(--bet-detail-win-border)]'
            : footerVariant === 'lose'
              ? 'border-[var(--bet-detail-lose-border)]'
              : 'border-[var(--ticket-info-card-border)]'
        } ${styles.cardMask}`}
      >
        {/* Main Content */}
        <div className="px-5 py-5 sm:px-12 sm:py-6">
          {/* Top Section: Bet Type, Row Number, Status */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mb-3 sm:mb-4">
            {/* Bet Type */}
            <div className="sm:w-1/3 flex items-center gap-2">
              <span className="text-xs text-[var(--bet-detail-row-label)]">
                {t('pollDetails.betType')}:
              </span>
              <span className="text-base sm:text-lg font-semibold text-[var(--bet-detail-bet-type)]">
                {betType}
              </span>
            </div>

            {/* Row Number & Status Row */}
            <div className="flex items-center justify-between sm:justify-center sm:w-1/3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--bet-detail-row-label)]">
                  {t('pollDetails.rowNumber')}:
                </span>
                <span className="text-lg sm:text-2xl font-bold text-[var(--bet-detail-row-value)]">
                  {padDigits(rowNumber)}
                </span>
              </div>

              {/* Status - visible on mobile in same row */}
              <div className="flex sm:hidden items-center gap-2">
                <span className="text-xs text-[var(--bet-detail-row-label)]">
                  {t('pollDetails.status')}:
                </span>
                <TicketStatusBadge status={status} />
              </div>
            </div>

            {/* Status - Desktop only, right aligned */}
            <div className="hidden sm:flex sm:w-1/3 items-center justify-end gap-2">
              <span className="text-xs text-[var(--bet-detail-row-label)]">
                {t('pollDetails.status')}:
              </span>
              <TicketStatusBadge status={status} />
            </div>
          </div>

          {/* Divider after top elements */}
          <hr className="border-t border-[var(--bet-detail-divider)] opacity-30 mb-3 sm:mb-4" />

          {/* Middle Section: Bet Number, Reward Result, Payout Rate / Max Reward */}
          <div className="flex items-center">
            {/* Bet Number (Large) - 40% on mobile, 50% on desktop */}
            <div className="w-2/5 sm:w-1/2 flex flex-col gap-1 pr-3 sm:pr-0">
              <span className="text-xs text-[var(--bet-detail-label)]">
                {t('pollDetails.betNumber')}:
              </span>
              <span className="text-3xl sm:text-5xl font-bold text-[var(--bet-detail-number)]">
                {betNumber}
              </span>
            </div>

            {/* Vertical Divider */}
            <div className="self-stretch border-l border-[var(--bet-detail-divider)] opacity-30" />

            {/* Reward Result & Payout Rate / Max Reward - 60% on mobile, 50% on desktop */}
            <div className="w-3/5 sm:w-1/2 flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-0 pl-4 sm:pl-0">
              {/* Reward Result */}
              <div className="flex-1 flex flex-col gap-0.5 sm:gap-1 items-center">
                <span className="text-xs text-[var(--bet-detail-label)]">
                  {t('pollDetails.rewardResult')}:
                </span>
                {/* Show waiting badge if no result and status is waiting */}
                {isEmptyRewardResult(rewardResult) &&
                status === TicketStatus.WAITING ? (
                  <TicketStatusBadge status={TicketStatus.WAITING} />
                ) : !isEmptyRewardResult(rewardResult) ? (
                  isThreeTodBet && multipleResults.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-0.5">
                      {multipleResults
                        .flatMap((row) => row.split(' '))
                        .map((result, index) => (
                          <span
                            key={index}
                            className="text-lg sm:text-2xl font-bold text-center"
                            style={{
                              color: 'var(--reward-result-accent)',
                            }}
                          >
                            {result}
                          </span>
                        ))}
                    </div>
                  ) : (
                    <span
                      className="text-xl sm:text-2xl font-bold"
                      style={{ color: 'var(--reward-result-accent)' }}
                    >
                      {rewardResult}
                    </span>
                  )
                ) : null}

                {/* Set lottery: Match type badge with i18n translation */}
                {isSetLottery && matchType && (
                  <span
                    className={`inline-block mt-1 px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                      matchType === 'no_match'
                        ? 'bg-[var(--set-lottery-badge-lose-bg)] text-[var(--set-lottery-badge-lose-text)]'
                        : 'bg-[var(--set-lottery-badge-win-bg)] text-[var(--set-lottery-badge-win-text)]'
                    }`}
                  >
                    {formatMatchType(matchType)}
                  </span>
                )}
              </div>

              {/* Payout Rate or Max Reward Amount (set lottery) */}
              <div className="flex-1 flex flex-col gap-0.5 sm:gap-1 items-center">
                {isSetLottery && maxRewardAmount !== undefined ? (
                  <>
                    <span className="text-xs text-[var(--bet-detail-label)]">
                      {t('pollDetails.maxRewardAmount', 'Max Reward Amount')}:
                    </span>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span className="text-lg sm:text-2xl font-bold text-[var(--bet-detail-value)]">
                        {maxRewardAmount.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                      <Tooltip
                        usePortal={false}
                        content={
                          <>
                            {t(
                              'pollDetails.maxRewardTooltip',
                              'Maximum payout if your ticket wins the highest prize tier.',
                            )}
                          </>
                        }
                      >
                        <FiInfo
                          className="w-4 h-4 text-[var(--ticket-info-card-top-border)] cursor-pointer"
                          aria-hidden
                        />
                      </Tooltip>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-xs text-[var(--bet-detail-label)]">
                      {t('pollDetails.payoutRate')}:
                    </span>
                    <div className="flex items-center gap-1 sm:gap-2">
                      {hasLimitedRate && (
                        <span className="text-lg sm:text-2xl font-bold line-through text-[var(--bet-detail-strikethrough)] opacity-50">
                          {payoutRate.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      )}
                      <span className="text-lg sm:text-2xl font-bold text-[var(--bet-detail-value)]">
                        {(hasLimitedRate
                          ? limitedPayoutRate
                          : payoutRate
                        )?.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                      <Tooltip
                        usePortal={false}
                        content={
                          <>
                            {t('pollDetails.payoutTooltipPrefix')}{' '}
                            <span
                              style={{
                                color: 'var(--tooltip-highlight)',
                                fontWeight: 600,
                              }}
                            >
                              {t('pollDetails.payoutTooltipHighlight')}
                            </span>
                            {t('pollDetails.payoutTooltipSuffix')}
                          </>
                        }
                      >
                        <FiInfo
                          className="w-4 h-4 text-[var(--ticket-info-card-top-border)] cursor-pointer"
                          aria-hidden
                        />
                      </Tooltip>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Set lottery: Collapsible winning numbers (hidden when waiting for results) */}
          {isSetLottery &&
            winningNumbers &&
            winningNumbers.length > 0 &&
            status !== TicketStatus.WAITING && (
              <WinningNumbersCollapsible
                winningNumbers={winningNumbers}
                className="mt-4"
              />
            )}
        </div>

        {/* Bottom Row: Bet Amount & Final Payout */}
        <div
          className={`relative px-5 py-4 sm:px-12 sm:py-5 flex items-center justify-between rounded-b-xl ${
            footerVariant === 'win'
              ? 'bg-[var(--bet-detail-win-bg)]'
              : footerVariant === 'lose'
                ? 'bg-[var(--bet-detail-lose-bg)]'
                : ''
          }`}
          style={
            footerVariant === 'default'
              ? {
                  borderTop: '1px dashed var(--ticket-info-divider)',
                  borderImage:
                    'repeating-linear-gradient(to right, var(--ticket-info-divider) 0, var(--ticket-info-divider) 10px, transparent 10px, transparent 20px) 1',
                }
              : undefined
          }
        >
          {/* Confetti overlay for win status */}
          {footerVariant === 'win' && (
            <div
              className="absolute top-0 left-3 right-3 sm:left-5 sm:right-5 bottom-0 pointer-events-none z-0 opacity-50 overflow-hidden"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='100' viewBox='0 0 800 100'%3E%3Crect x='23' y='12' width='3' height='8' fill='%23ffffff' transform='rotate(42 24.5 16)'/%3E%3Crect x='89' y='67' width='2.5' height='7' fill='%23ffffff' transform='rotate(-28 90.25 70.5)'/%3E%3Crect x='156' y='31' width='3' height='8' fill='%23ffffff' transform='rotate(73 157.5 35)'/%3E%3Crect x='234' y='78' width='2.5' height='7' fill='%23ffffff' transform='rotate(-15 235.25 81.5)'/%3E%3Crect x='312' y='18' width='3' height='8' fill='%23ffffff' transform='rotate(56 313.5 22)'/%3E%3Crect x='378' y='52' width='2.5' height='7' fill='%23ffffff' transform='rotate(-67 379.25 55.5)'/%3E%3Crect x='467' y='8' width='3' height='8' fill='%23ffffff' transform='rotate(31 468.5 12)'/%3E%3Crect x='523' y='71' width='2.5' height='7' fill='%23ffffff' transform='rotate(-48 524.25 74.5)'/%3E%3Crect x='612' y='25' width='3' height='8' fill='%23ffffff' transform='rotate(82 613.5 29)'/%3E%3Crect x='689' y='58' width='2.5' height='7' fill='%23ffffff' transform='rotate(-35 690.25 61.5)'/%3E%3Crect x='756' y='15' width='3' height='8' fill='%23ffffff' transform='rotate(19 757.5 19)'/%3E%3Crect x='45' y='85' width='2.5' height='7' fill='%23ffffff' transform='rotate(63 46.25 88.5)'/%3E%3Crect x='198' y='45' width='3' height='8' fill='%23ffffff' transform='rotate(-52 199.5 49)'/%3E%3Crect x='423' y='38' width='2.5' height='7' fill='%23ffffff' transform='rotate(27 424.25 41.5)'/%3E%3Crect x='567' y='82' width='3' height='8' fill='%23ffffff' transform='rotate(-71 568.5 86)'/%3E%3Crect x='645' y='42' width='2.5' height='7' fill='%23ffffff' transform='rotate(44 646.25 45.5)'/%3E%3C/svg%3E")`,
                backgroundSize: '800px 100px',
                backgroundRepeat: 'repeat-x',
              }}
            />
          )}

          {/* Bet Amount */}
          <div className="relative z-10 flex flex-col gap-1">
            <span
              className={`text-sm font-semibold ${
                footerVariant === 'win'
                  ? 'text-[var(--bet-detail-win-text-muted)]'
                  : footerVariant === 'lose'
                    ? 'text-[var(--bet-detail-lose-text-muted)]'
                    : 'text-[var(--bet-detail-payout-label)]'
              }`}
            >
              {t('pollDetails.betAmount')}:
            </span>
            <span
              className={`text-lg sm:text-xl font-bold ${
                footerVariant === 'win'
                  ? 'text-[var(--bet-detail-win-text)]'
                  : footerVariant === 'lose'
                    ? 'text-[var(--bet-detail-lose-text)]'
                    : 'text-[var(--bet-detail-payout-value)]'
              }`}
            >
              {betAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>

          {/* Final Payout */}
          <div className="relative z-10 flex flex-col gap-1">
            <span
              className={`text-sm font-semibold ${
                footerVariant === 'win'
                  ? 'text-[var(--bet-detail-win-text-muted)]'
                  : footerVariant === 'lose'
                    ? 'text-[var(--bet-detail-lose-text-muted)]'
                    : 'text-[var(--bet-detail-payout-label)]'
              }`}
            >
              {t('pollDetails.finalPayout')}:
            </span>
            <span
              className={`text-lg sm:text-xl font-bold ${
                footerVariant === 'win'
                  ? 'text-[var(--bet-detail-win-text)]'
                  : footerVariant === 'lose'
                    ? 'text-[var(--bet-detail-lose-text)]'
                    : 'text-[var(--bet-detail-payout-value)]'
              }`}
            >
              {computedPayout.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BetDetailCard
